import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { COMPANY, PRODUCTS } from "@/data/products";
import { Mail, Phone, MessageCircle, MapPin, Send, CheckCircle2 } from "lucide-react";

const initial = {
  name: "",
  company: "",
  email: "",
  phone: "",
  country: "",
  quantity_mt: "",
  product: "",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Embed product interest at top of message if provided.
      const composedMessage = form.product
        ? `[Product of interest: ${form.product}]\n\n${form.message}`
        : form.message;

      const payload = {
        name: form.name,
        company: form.company || undefined,
        email: form.email,
        phone: form.phone || undefined,
        country: form.country || undefined,
        quantity_mt: form.quantity_mt
          ? parseFloat(form.quantity_mt)
          : undefined,
        message: composedMessage,
      };
      Object.keys(payload).forEach((k) => {
        if (payload[k] === undefined) delete payload[k];
      });
      await api.post("/inquiries", payload);
      toast.success("Inquiry submitted. We'll respond within 24 hours.");
      setSubmitted(true);
      setForm(initial);
    } catch (err) {
      const detail =
        err?.response?.data?.detail || "Could not submit. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Validation error.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-contact" className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <p className="label-eyebrow">/ Contact</p>
          <h1 className="font-heading mt-4 text-4xl md:text-6xl font-black uppercase tracking-tighter">
            Start a <br /> <span className="copper-text">direct trade.</span>
          </h1>
          <p className="mt-6 text-zinc-400 leading-relaxed max-w-md">
            Send us your requirement — product, tonnage, destination port and
            incoterms. Our desk responds within 24 hours with availability,
            indicative pricing and documentation.
          </p>

          <div className="mt-12 space-y-5">
            <ContactRow icon={MapPin} label="Headquarters" value={COMPANY.address + ", " + COMPANY.country} testid="contact-info-location" />
            <ContactRow
              icon={Mail}
              label="Email"
              value={COMPANY.email}
              href={`mailto:${COMPANY.email}`}
              testid="contact-info-email"
            />
            <ContactRow
              icon={MessageCircle}
              iconClass="text-green-500"
              label="WhatsApp · Ireland"
              value={COMPANY.whatsappPrimary}
              href={`https://wa.me/${COMPANY.whatsappPrimaryDigits}`}
              testid="contact-info-wa-ireland"
            />
            <ContactRow
              icon={Phone}
              label="WhatsApp / Phone · Zambia"
              value={COMPANY.phoneZambia}
              href={`tel:${COMPANY.phoneZambia}`}
              testid="contact-info-phone-zm"
            />
            <ContactRow
              icon={MessageCircle}
              iconClass="text-green-500"
              label="WhatsApp · Zambia"
              value={COMPANY.phoneZambia2}
              href={`https://wa.me/${COMPANY.phoneZambia2Digits}`}
              testid="contact-info-phone-zm2"
            />
          </div>

          <div className="mt-10 border border-zinc-800">
            <div className="px-5 py-3 border-b border-zinc-800 bg-zinc-900">
              <p className="label-eyebrow text-zinc-300">Operational Sites</p>
            </div>
            <ul className="divide-y divide-zinc-800">
              {COMPANY.sites.map((s, i) => (
                <li
                  key={s.name}
                  data-testid={`contact-site-${i}`}
                  className="px-5 py-3 flex items-start gap-3 text-sm"
                >
                  <MapPin size={14} className="mt-0.5 copper-text shrink-0" />
                  <div>
                    <p className="font-heading font-bold text-zinc-200">
                      {s.name}
                    </p>
                    <p className="text-zinc-500">{s.address}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="border border-zinc-800 p-6 md:p-10 bg-[var(--eel-surface)]">
            {submitted ? (
              <div data-testid="contact-success" className="py-12 text-center">
                <CheckCircle2 size={56} className="copper-text mx-auto" />
                <h2 className="font-heading mt-6 text-3xl font-black uppercase tracking-tight">
                  Inquiry Received
                </h2>
                <p className="mt-3 text-zinc-400 max-w-md mx-auto">
                  Thank you. Our trade desk will reach out within 24 hours with
                  availability and next steps.
                </p>
                <button
                  data-testid="contact-success-new"
                  onClick={() => setSubmitted(false)}
                  className="mt-8 px-6 py-3 border border-zinc-700 text-sm uppercase tracking-widest hover:border-white"
                >
                  Submit Another
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-6" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field id="name" label="Full Name *" value={form.name} onChange={update("name")} required minLength={2} placeholder="Jane Doe" />
                  <Field id="company" label="Company" value={form.company} onChange={update("company")} placeholder="Acme Refineries Ltd." />
                  <Field id="email" type="email" label="Email *" value={form.email} onChange={update("email")} required placeholder="jane@company.com" />
                  <Field id="phone" label="Phone / WhatsApp" value={form.phone} onChange={update("phone")} placeholder="+1 555 123 4567" />
                  <Field id="country" label="Country" value={form.country} onChange={update("country")} placeholder="United Arab Emirates" />
                  <Field id="quantity_mt" type="number" label="Quantity Needed (MT)" value={form.quantity_mt} onChange={update("quantity_mt")} min={0} step="any" placeholder="50" />
                </div>

                <div>
                  <label className="label-eyebrow text-zinc-400 block mb-2" htmlFor="product">
                    Product of Interest
                  </label>
                  <select
                    id="product"
                    data-testid="contact-input-product"
                    value={form.product}
                    onChange={update("product")}
                    className="w-full bg-zinc-900 border border-zinc-800 px-4 py-3 text-sm text-zinc-100 focus:outline-none"
                  >
                    <option value="">-- Select a product --</option>
                    {PRODUCTS.map((p) => (
                      <option key={p.slug} value={p.name}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="label-eyebrow text-zinc-400 block mb-2" htmlFor="message">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    data-testid="contact-input-message"
                    required
                    minLength={5}
                    rows={6}
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Specify grade, target tonnage per month, destination port, payment terms..."
                    className="w-full bg-zinc-900 border border-zinc-800 px-4 py-3 text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit"
                  className="w-full md:w-auto px-8 py-4 copper-bg text-black font-semibold uppercase tracking-widest text-sm hover:bg-[var(--eel-copper-hover)] transition-colors inline-flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? "Submitting..." : (
                    <>
                      Submit Inquiry <Send size={14} />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ContactRow({ icon: Icon, iconClass = "copper-text", label, value, href, testid }) {
  const content = (
    <>
      <div className="w-10 h-10 border border-zinc-800 grid place-items-center shrink-0">
        <Icon size={16} className={iconClass} />
      </div>
      <div className="min-w-0">
        <p className="label-eyebrow text-zinc-500">{label}</p>
        <p className="mt-1 font-heading font-bold text-sm md:text-base break-all">
          {value}
        </p>
      </div>
    </>
  );
  return href ? (
    <a
      href={href}
      target={href.startsWith("http") ? "_blank" : undefined}
      rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
      className="flex items-start gap-4 group hover:text-[var(--eel-copper)]"
      data-testid={testid}
    >
      {content}
    </a>
  ) : (
    <div className="flex items-start gap-4" data-testid={testid}>
      {content}
    </div>
  );
}

function Field({ id, label, ...rest }) {
  return (
    <div>
      <label className="label-eyebrow text-zinc-400 block mb-2" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        data-testid={`contact-input-${id}`}
        {...rest}
        className="w-full bg-zinc-900 border border-zinc-800 px-4 py-3 text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none"
      />
    </div>
  );
}
